// Container-width layout mode (NOT a viewport breakpoint). Measures the
// referenced element's own width via ResizeObserver and maps it to a named
// mode through `decide(width)`. Powers content-fit layouts like the dashboard
// alerts row and the manage-account cards, which reflow based on their own
// available width (e.g. when the R.I.S.A side panel opens), not the window.
import { useEffect, useState } from 'react';

/**
 * @param {React.RefObject<HTMLElement>} ref   element to measure
 * @param {(width:number) => string} decide    width → mode mapper
 * @param {string} initial                      mode before first measure
 */
export function useContainerMode(ref, decide, initial = 'desktop') {
  const [mode, setMode] = useState(initial);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      if (!el.isConnected) return;
      setMode(decide(el.getBoundingClientRect().width));
    };

    measure();
    const raf = requestAnimationFrame(measure); // re-measure after fonts/layout settle
    window.addEventListener('resize', measure);

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  return mode;
}

export default useContainerMode;
